import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { dataverseClient, TABLES } from '@shared/api';
import { assertGuid } from '@shared/lib';
import { useAuth } from '@shared/providers/AuthProvider';
import type { ContractYear, ContractYearQueryParams } from '../model/types';
import { CONTRACT_YEAR_STATUS_FROM_VALUE, CONTRACT_YEAR_STATUS_VALUES } from '../model/types';

// =============================================================================
// Entity Interface
// =============================================================================

interface ContractYearEntity {
  vba_contractyearid: string;
  vba_name: string;
  _vba_account_value?: string;
  vba_startdate?: string;
  vba_enddate?: string;
  vba_status?: number;
  _vba_previouscontractyear_value?: string;
  _vba_renewalowner_value?: string;
  _vba_opportunity_value?: string;
  vba_terminationreason?: string;
  vba_lives?: number;
  vba_revenue?: number;
  vba_notes?: string;
  vba_confirmedby?: string;
  vba_confirmedon?: string;
  _vba_broker_value?: string;
  vba_brokercontactname?: string;
  vba_brokeragencyname?: string;
  createdon?: string;
  modifiedon?: string;
  // Formatted values from annotations
  [key: string]: unknown;
}

// =============================================================================
// Mappers
// =============================================================================

function mapToDomain(entity: ContractYearEntity): ContractYear {
  return {
    id: entity.vba_contractyearid,
    name: entity.vba_name ?? '',
    accountId: entity._vba_account_value ?? '',
    accountName: (entity['_vba_account_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    startDate: entity.vba_startdate ?? undefined,
    endDate: entity.vba_enddate ?? undefined,
    status: entity.vba_status != null
      ? (CONTRACT_YEAR_STATUS_FROM_VALUE[entity.vba_status] ?? 'draft')
      : 'draft',
    previousContractYearId: entity._vba_previouscontractyear_value ?? undefined,
    renewalOwnerId: entity._vba_renewalowner_value ?? undefined,
    renewalOwnerName: (entity['_vba_renewalowner_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    opportunityId: entity._vba_opportunity_value ?? undefined,
    opportunityName: (entity['_vba_opportunity_value@OData.Community.Display.V1.FormattedValue'] as string) ?? undefined,
    terminationReason: entity.vba_terminationreason ?? undefined,
    lives: entity.vba_lives ?? undefined,
    revenue: entity.vba_revenue ?? undefined,
    notes: entity.vba_notes ?? undefined,
    confirmedBy: entity.vba_confirmedby ?? undefined,
    confirmedOn: entity.vba_confirmedon ?? undefined,
    brokerId: entity._vba_broker_value ?? undefined,
    // Snapshot first; the lookup's formatted value covers a stamp with no snapshot.
    brokerName: entity.vba_brokercontactname
      ?? (entity['_vba_broker_value@OData.Community.Display.V1.FormattedValue'] as string | undefined)
      ?? undefined,
    brokerAgencyName: entity.vba_brokeragencyname ?? undefined,
    createdOn: entity.createdon ?? undefined,
    modifiedOn: entity.modifiedon ?? undefined,
  };
}

function mapToEntity(input: Partial<ContractYear>, accountId?: string): Record<string, unknown> {
  const entity: Record<string, unknown> = {};

  if (input.name !== undefined) entity.vba_name = input.name;
  if (input.startDate !== undefined) entity.vba_startdate = input.startDate ?? null;
  if (input.endDate !== undefined) entity.vba_enddate = input.endDate ?? null;
  if (input.status !== undefined) entity.vba_status = CONTRACT_YEAR_STATUS_VALUES[input.status];
  if (input.terminationReason !== undefined) entity.vba_terminationreason = input.terminationReason;
  if (input.lives !== undefined) entity.vba_lives = input.lives;
  if (input.revenue !== undefined) entity.vba_revenue = input.revenue;
  if (input.notes !== undefined) entity.vba_notes = input.notes;
  if (input.confirmedBy !== undefined) entity.vba_confirmedby = input.confirmedBy;
  if (input.confirmedOn !== undefined) entity.vba_confirmedon = input.confirmedOn;

  // Lookups — validate all GUIDs before interpolation
  const acctId = accountId ?? input.accountId;
  if (acctId) entity['vba_Account@odata.bind'] = `/accounts(${assertGuid(acctId, 'accountId')})`;
  if (input.previousContractYearId !== undefined) {
    entity['vba_PreviousContractYear@odata.bind'] = input.previousContractYearId
      ? `/vba_contractyears(${assertGuid(input.previousContractYearId, 'previousContractYearId')})` : null;
  }
  if (input.renewalOwnerId !== undefined) {
    entity['vba_RenewalOwner@odata.bind'] = input.renewalOwnerId
      ? `/systemusers(${assertGuid(input.renewalOwnerId, 'renewalOwnerId')})` : null;
  }
  if (input.brokerId !== undefined) {
    entity['vba_Broker@odata.bind'] = input.brokerId
      ? `/contacts(${assertGuid(input.brokerId, 'brokerId')})` : null;
  }
  if (input.brokerName !== undefined) entity.vba_brokercontactname = input.brokerName || null;
  if (input.brokerAgencyName !== undefined) entity.vba_brokeragencyname = input.brokerAgencyName || null;
  if (input.opportunityId !== undefined) {
    entity['vba_Opportunity@odata.bind'] = input.opportunityId
      ? `/opportunities(${assertGuid(input.opportunityId, 'opportunityId')})` : null;
  }

  return entity;
}

// =============================================================================
// API Functions
// =============================================================================

async function list(params: ContractYearQueryParams = {}): Promise<ContractYear[]> {
  const filters: string[] = [];
  if (params.accountId) filters.push(`_vba_account_value eq '${assertGuid(params.accountId, 'accountId')}'`);
  if (params.status) filters.push(`vba_status eq ${CONTRACT_YEAR_STATUS_VALUES[params.status]}`);

  const orderBy = params.orderBy === 'startDate' ? 'vba_startdate'
    : params.orderBy === 'endDate' ? 'vba_enddate'
    : 'modifiedon';

  // listAll: the unfiltered call (My Groups / Search pull every year in the
  // org) will cross the 5000-row single-page cap as the book grows — plain
  // list() would silently truncate. Account-scoped calls stay one page.
  const rows = await dataverseClient.listAll<ContractYearEntity>(TABLES.CONTRACT_YEAR, {
    filter: filters.length > 0 ? filters.join(' and ') : undefined,
    orderBy: `${orderBy} ${params.orderDirection ?? 'desc'}`,
  });

  return rows.map(mapToDomain);
}

async function get(id: string): Promise<ContractYear> {
  const entity = await dataverseClient.get<ContractYearEntity>(TABLES.CONTRACT_YEAR, id);
  return mapToDomain(entity);
}

async function create(input: Partial<ContractYear>): Promise<ContractYear> {
  const entity = mapToEntity(input, input.accountId);
  const response = await dataverseClient.create<ContractYearEntity>(TABLES.CONTRACT_YEAR, entity);
  return mapToDomain(response);
}

async function update(id: string, input: Partial<ContractYear>): Promise<void> {
  const entity = mapToEntity(input);
  await dataverseClient.update(TABLES.CONTRACT_YEAR, id, entity);
}

async function remove(id: string): Promise<void> {
  await dataverseClient.remove(TABLES.CONTRACT_YEAR, id);
}

// =============================================================================
// React Query Hooks
// =============================================================================

const keys = {
  all: ['contractYears'] as const,
  lists: () => [...keys.all, 'list'] as const,
  list: (params: ContractYearQueryParams) => [...keys.lists(), params] as const,
  detail: (id: string) => [...keys.all, 'detail', id] as const,
};

export function useContractYears(
  params: ContractYearQueryParams = {},
  options?: Omit<UseQueryOptions<ContractYear[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () => list(params),
    ...options,
  });
}

export function useContractYear(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => get(id),
    enabled: !!id,
  });
}

export function useContractYearsByAccount(accountId: string) {
  return useQuery({
    queryKey: keys.list({ accountId }),
    queryFn: () => list({ accountId, orderBy: 'startDate', orderDirection: 'desc' }),
    enabled: !!accountId,
  });
}

export function useCreateContractYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: create,
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

export function useUpdateContractYear() {
  const qc = useQueryClient();
  const { displayName } = useAuth();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ContractYear> }) => {
      // Stamp who/when on the draft→confirmed transition (durable audit).
      const stamped =
        input.status === 'confirmed'
          ? { ...input, confirmedBy: displayName ?? 'Unknown', confirmedOn: new Date().toISOString() }
          : input;
      return update(id, stamped);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}

export function useDeleteContractYear() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: remove,
    onSuccess: () => { qc.invalidateQueries({ queryKey: keys.all }); },
  });
}
